'use client'

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
} from '@chakra-ui/react'
import type { ColumnDef } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { FileText, Plus } from 'lucide-react'
import {
  useGetDonorCardDetailQuery,
  useGetDonorDetailQuery,
  useGetDonorDocumentsQuery,
  useOpenDonorDocumentFileMutation,
} from '@entities/donor/api'
import type { IDonorDocument } from '@entities/donor/model'
import { AddDonorDocumentModal } from '@features/donor-documents'
import { AppCard } from '@/shared/ui/card'
import { DataTable } from '@/shared/ui/data-table'
import { toaster } from '@/shared/ui'
import { isDonorCardReadOnly } from '../model/use-donor-card-read-only'

interface Props {
  visitId: string
}

const formatDate = (value?: string) =>
  value ? dayjs(value).format('DD.MM.YYYY') : '—'

/**
 * Certificates (справки) on file for a female donor. The endpoint rejects any
 * other donor, so nothing is requested until the card confirms the gender.
 */
export function DocumentsTab({ visitId }: Props) {
  const { t } = useTranslation()
  const [addOpen, setAddOpen] = useState(false)

  const { data: donorDetail } = useGetDonorDetailQuery(visitId)
  const donorId = donorDetail?.donor_id ?? ''
  const { data: donorCard } = useGetDonorCardDetailQuery(donorId)
  const isFemale = donorCard?.donor_info.gender === 'FEMALE'
  // Viewing and opening files stays available; adding does not.
  const readOnly = isDonorCardReadOnly(donorCard)

  const { data, isLoading } = useGetDonorDocumentsQuery(donorId, isFemale)
  const openFile = useOpenDonorDocumentFileMutation(donorId)

  const columns = useMemo<ColumnDef<IDonorDocument>[]>(() => {
    const textCell = (content: React.ReactNode) => (
      <Text py={3} px={4} fontSize="sm" color="fg.muted">
        {content}
      </Text>
    )

    return [
      {
        accessorKey: 'date',
        header: () => t('donor_detail.documents.table.date'),
        cell: (info) => textCell(formatDate(info.getValue<string>())),
      },
      {
        accessorKey: 'type_label',
        header: () => t('donor_detail.documents.table.type'),
        cell: (info) => textCell(info.getValue<string | undefined>() || '—'),
      },
      {
        accessorKey: 'doctor',
        header: () => t('donor_detail.documents.table.doctor'),
        cell: (info) => textCell(info.getValue<string | undefined>() || '—'),
      },
      {
        accessorKey: 'note',
        header: () => t('donor_detail.documents.table.note'),
        cell: (info) => textCell(info.getValue<string | undefined>() || '—'),
      },
      {
        accessorKey: 'expiry_date',
        header: () => t('donor_detail.documents.table.expiry_date'),
        cell: (info) => textCell(formatDate(info.getValue<string>())),
      },
      {
        accessorKey: 'status',
        header: () => t('donor_detail.documents.table.status'),
        cell: (info) => {
          const row = info.row.original
          const active = row.status === 'ACTIVE'
          return (
            <Box py={3} px={4}>
              <Badge
                px={2}
                py={0.5}
                borderRadius="md"
                fontSize="11px"
                fontWeight="medium"
                bg={active ? 'badge.green.20' : 'badge.gray.20'}
                color={active ? 'badge.green.100' : 'badge.gray.100'}
              >
                {row.status_label || row.status}
              </Badge>
            </Box>
          )
        },
      },
      {
        id: 'file',
        header: () => t('donor_detail.documents.table.file'),
        cell: (info) => {
          const row = info.row.original
          if (!row.file_id) return textCell('—')
          const opening = openFile.isPending && openFile.variables === row.id
          return (
            <Box py={2} px={4}>
              <IconButton
                variant="ghost"
                size="xs"
                color="fg.muted"
                aria-label={
                  row.file_name ?? t('donor_detail.documents.table.file')
                }
                title={row.file_name}
                loading={opening}
                onClick={() =>
                  openFile.mutate(row.id, {
                    onError: () =>
                      toaster.error({
                        title: t('donor_detail.documents.file_error'),
                      }),
                  })
                }
                _hover={{ color: 'fg', bg: 'bg.muted' }}
              >
                <FileText size={15} />
              </IconButton>
            </Box>
          )
        },
      },
    ]
  }, [t, openFile.isPending, openFile.variables, openFile.mutate])

  if (donorCard && !isFemale) {
    return (
      <AppCard.Root>
        <Text p={5} fontSize="sm" color="fg.muted">
          {t('donor_detail.documents.female_only')}
        </Text>
      </AppCard.Root>
    )
  }

  return (
    <AppCard.Root>
      <Flex p={5} pb={0} align="center" justify="space-between" gap={3}>
        <Heading size="md" fontWeight="semibold">
          {t('donor_detail.documents.title')}
        </Heading>
        {!readOnly && (
          <Button
            size="sm"
            bg="primary.solid"
            color="white"
            borderRadius="md"
            gap={1}
            _hover={{ opacity: 0.85 }}
            _active={{ opacity: 0.7 }}
            disabled={!donorId}
            onClick={() => setAddOpen(true)}
          >
            <Plus size={14} />
            {t('donor_detail.documents.add')}
          </Button>
        )}
      </Flex>

      <Box p={5} pt={4}>
        <DataTable<IDonorDocument>
          data={data ?? []}
          columns={columns}
          isLoading={isLoading || !donorCard}
          emptyLabel={t('donor_detail.documents.empty')}
          renderPaginationLabel={(start, end, total) =>
            t('dashboard.pagination.showing', { start, end, total })
          }
        />
      </Box>

      {donorId && !readOnly && (
        <AddDonorDocumentModal
          open={addOpen}
          donorId={donorId}
          onClose={() => setAddOpen(false)}
        />
      )}
    </AppCard.Root>
  )
}
